import { useState } from 'react';

import { faGear } from '@fortawesome/free-solid-svg-icons';
import { Trans } from '@lingui/react/macro';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import { useAtom } from 'jotai';

import { VideaTypography } from '@/common-components/elements/VideaTypography/VideaTypography';
import { ToolbarButton } from '@/modules/annotator/imageToolbar/ToolbarButton';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { usePreferences } from '../../../../react-query/usePreferences';

import {
  selectedAdjustmentTypeAtom,
  selectedImageIdAtom,
  selectedPatientIdAtom,
  selectedVisitAtom,
} from './atoms';
import { FilterComparisonPreview } from './FilterComparisonPreview';
import { FilterSelectionModalFooter } from './FilterSelectionModalFooter';
import { ImageCarousel } from './ImageCarousel';
import { VisitSelector } from './VisitSelector';

// Modal dialog for configuring sharpening filter
export const SharpnessFilterSelectionModal = ({
  isInImageToolbar = false,
}: {
  isInImageToolbar?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useAtom(selectedVisitAtom);
  const [selectedPatientId, setSelectedPatientId] = useAtom(
    selectedPatientIdAtom,
  );
  const [selectedImageId, setSelectedImageId] = useAtom(selectedImageIdAtom);
  const [selectedAdjustmentType, setSelectedAdjustmentType] = useAtom(
    selectedAdjustmentTypeAtom,
  );
  const isHD1Selected = (selectedAdjustmentType ?? 'HD1') === 'HD1';
  const selectedHDVersion = isHD1Selected ? 'HD1' : 'HD2';

  const { data: preferences } = usePreferences();
  const { trackEvent } = useTrackEvent();

  const comparisonStepNumber = preferences?.enableSharpeningHd2Selection
    ? 4
    : 3;

  const onClose = () => {
    setIsOpen(false);
    setSelectedAdjustmentType('HD1');
    setSelectedVisit(undefined);
    setSelectedPatientId(undefined);
    setSelectedImageId(undefined);
  };

  return (
    <>
      {isInImageToolbar ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <ToolbarButton
                icon={faGear}
                isActive={isOpen}
                onClick={() => {
                  setIsOpen(true);
                  trackEvent(
                    mixpanelEvents.sharpeningFilterSelectionModalOpen,
                    {
                      source: 'imageToolbar',
                    },
                  );
                }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent
            variant="dark"
            side="top"
            sideOffset={8}
            alignOffset={-4}
          >
            <p>
              <Trans>Configure Sharpening Filter</Trans>
            </p>
          </TooltipContent>
        </Tooltip>
      ) : (
        <Button
          variant="outline"
          onClick={() => {
            setIsOpen(true);
            trackEvent(mixpanelEvents.sharpeningFilterSelectionModalOpen, {
              source: 'practicePreferencesMenu',
            });
          }}
        >
          <Trans>Configure Sharpening Filter</Trans>
        </Button>
      )}

      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="vai-max-w-6xl vai-max-h-[95vh] vai-overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <Trans>Image Sharpening Configuration</Trans>
            </DialogTitle>
          </DialogHeader>

          <div className="vai-flex vai-flex-col vai-gap-4 vai-w-full">
            {/* Instructions */}
            <div className="vai-flex vai-flex-col vai-gap-2">
              <div className="vai-flex vai-items-center vai-gap-2 vai-text-gray-600 vai-text-sm">
                <Trans>
                  Select a recent visit, choose an image, and compare different
                  sharpening filters side-by-side to determine your preferred
                  setting.
                </Trans>
              </div>
              <div className="vai-p-3 vai-rounded-md vai-bg-orange-50 vai-border vai-border-orange-200 vai-mt-2">
                <div className="vai-text-orange-800 vai-text-sm">
                  <Trans>
                    Note: This update will only affect images taken starting now
                    and will not automatically rerun all historical images. You
                    can manually rerun individual images if needed.
                  </Trans>
                </div>
              </div>
            </div>

            {/* Step 1: Visit Selection */}
            <div className="vai-flex vai-flex-col vai-gap-3">
              <VideaTypography variant="labelSmall">
                <Trans>Step 1: Select a Recent Visit</Trans>
              </VideaTypography>
              <VisitSelector />
            </div>

            {/* Step 2: Image Carousel (only show if visit selected) */}
            {selectedVisit && selectedPatientId && (
              <div className="vai-flex vai-flex-col vai-gap-3">
                <div>
                  <Trans>Step 2: Select an Image</Trans>
                </div>
                <ImageCarousel />
              </div>
            )}

            {/* Step 3: Compare Filters (only when HD2 selection enabled) */}
            {selectedImageId &&
              selectedVisit &&
              selectedPatientId &&
              preferences?.enableSharpeningHd2Selection && (
                <div className="vai-flex vai-flex-col vai-gap-2">
                  <div>
                    <Trans>Step 3: Compare Filters</Trans>
                  </div>

                  <div className="vai-text-sm">
                    <Trans>
                      Choose the filter you&apos;d like to compare to the
                      original. You&apos;ll review side-by-side in the next
                      step.
                    </Trans>
                  </div>

                  <div className="vai-w-fit">
                    <Select
                      value={selectedHDVersion}
                      onValueChange={(v: 'HD1' | 'HD2') =>
                        setSelectedAdjustmentType(v)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['HD1', 'HD2']?.map((filter) => (
                          <SelectItem key={filter} value={filter}>
                            <span className="vai-mr-2">
                              {filter === 'HD1' ? (
                                <Trans>HD1 Filter</Trans>
                              ) : (
                                <Trans>HD2 Filter</Trans>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

            {selectedImageId && selectedVisit && selectedPatientId && (
              <div className="vai-flex vai-flex-col vai-gap-3">
                <div>
                  <Trans>
                    Step {comparisonStepNumber}: Compare Filters Side-by-Side
                  </Trans>
                </div>
                <FilterComparisonPreview />
              </div>
            )}

            <FilterSelectionModalFooter onCancel={onClose} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
